import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { formatGBP } from "../lib/format";
import { MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Autocomplete-powered search input.
 * Debounces API calls, shows a suggestion dropdown, routes on click/enter.
 */
export default function SearchAutocomplete({ initial = "", placeholder = "Search garri, egusi, jollof kits...", onSubmit }) {
  const [query, setQuery] = useState(initial);
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [highlight, setHighlight] = useState(-1);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  // Debounced suggest
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/products/search/suggest?q=${encodeURIComponent(q)}`);
        setResults(data?.suggestions || []);
        setOpen(true);
        setHighlight(-1);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [query]);

  // Click-outside close
  useEffect(() => {
    const fn = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const submit = (e) => {
    if (e) e.preventDefault();
    setOpen(false);
    if (onSubmit) onSubmit(query);
  };

  const pickSuggestion = (s) => {
    setOpen(false);
    setQuery(s.name);
    navigate(`/products/${s.product_id}`);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) {
      if (e.key === "Enter") submit(e);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, -1));
    } else if (e.key === "Enter") {
      if (highlight >= 0) {
        e.preventDefault();
        pickSuggestion(results[highlight]);
      } else {
        submit(e);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} className="relative flex-1" data-testid="search-autocomplete">
      <form onSubmit={submit} className="flex items-center gap-2 bg-white border-2 border-[#E5E2D9] rounded-full px-4 py-2.5 focus-within:border-[#A94438]">
        <MagnifyingGlass size={18} weight="bold" className="text-[#5C5C54] flex-shrink-0" />
        <input
          data-testid="product-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => query.trim().length >= 2 && results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-[#5C5C54]/60"
          autoComplete="off"
        />
        <button type="submit" data-testid="product-search-btn" className="rounded-full bg-[#1A1A18] text-white text-xs font-bold px-4 py-2 hover:bg-[#A94438] transition-all">
          Search
        </button>
      </form>

      {open && (
        <div
          data-testid="search-suggestions"
          className="absolute left-0 right-0 top-[calc(100%+6px)] z-40 bg-white border border-[#E5E2D9] rounded-2xl shadow-[0_20px_40px_rgba(0,0,0,0.08)] overflow-hidden"
        >
          {loading && results.length === 0 && (
            <p className="px-4 py-3 text-xs text-[#5C5C54]">Searching…</p>
          )}
          {!loading && results.length === 0 && (
            <p className="px-4 py-3 text-xs text-[#5C5C54]">No matches. Try "garri", "egusi" or "jollof".</p>
          )}
          {results.map((s, i) => (
            <button
              key={s.product_id}
              type="button"
              data-testid="search-suggestion-item"
              onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all ${highlight === i ? "bg-[#F2EFE9]" : "hover:bg-[#F9F7F3]"}`}
            >
              <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#F2EFE9] flex-shrink-0">
                {s.image && <img src={s.image} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{s.name}</p>
                <p className="text-xs text-[#5C5C54] truncate">{s.vendor_name} · {s.category}</p>
              </div>
              <span className="text-xs font-bold text-[#A94438] flex-shrink-0">{formatGBP(s.price)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
